import axios, { type AxiosResponse } from "axios";
import { logAPIEvent } from "@fmc-digital/core-utils";
import { config } from "@/config";
import { buildRegistrationFeedbackPayload } from "./buildRegistrationFeedbackPayload";
import { getClientDeviceInfo } from "./getClientDeviceInfo";
import type {
  RegistrationFeedbackApiPayload,
  RegistrationFeedbackMetadata,
} from "./registrationFeedback.types";

export interface RegistrationFeedbackOptions {
  feedbackText?: string;
  userUuid?: string;
  loanNumber?: string;
}

export interface RegistrationQualtricsPayload {
  values: RegistrationFeedbackApiPayload;
}

export interface QualtricsApiMeta {
  httpStatus?: string;
  requestId?: string;
  error?: {
    errorMessage?: string;
    errorCode?: string;
  };
}

export interface QualtricsCreateResponseResult {
  responseId?: string;
}

export interface QualtricsApiResponse {
  result?: QualtricsCreateResponseResult;
  meta?: QualtricsApiMeta;
}

export interface RegistrationFeedbackResult {
  statusCode: number;
  responseId?: string;
  requestId?: string;
}

function isQualtricsSuccessStatus(statusCode: number): boolean {
  return statusCode >= 200 && statusCode < 300;
}

function getQualtricsErrorMessage(
  statusCode: number,
  data?: QualtricsApiResponse,
): string {
  const metaError = data?.meta?.error;
  if (metaError?.errorMessage) {
    return metaError.errorMessage;
  }

  switch (statusCode) {
    case 400:
      return "Invalid survey submission request";
    case 401:
      return "Qualtrics authentication failed";
    case 403:
      return "Qualtrics access denied";
    case 404:
      return "Qualtrics survey not found";
    case 429:
      return "Qualtrics rate limit exceeded";
    default:
      if (statusCode >= 500) {
        return "Qualtrics service unavailable";
      }
      return `Qualtrics request failed with status ${statusCode}`;
  }
}

function logQualtricsApiResponse(
  url: string,
  success: boolean,
  statusCode: number,
  details?: { responseId?: string; errorMessage?: string },
): void {
  const description = success
    ? `status=${statusCode}${details?.responseId ? `, responseId=${details.responseId}` : ""}`
    : undefined;

  logAPIEvent({
    action: "APIResponse",
    type: "API",
    success: success ? "true" : "false",
    request: url,
    ...(description ? { description } : {}),
    ...(details?.errorMessage
      ? { error: statusCode > 0 ? `${statusCode}: ${details.errorMessage}` : details.errorMessage }
      : {}),
  });
}

function warnIfMetadataIncomplete(metadata: Partial<RegistrationFeedbackMetadata>): void {
  if (!metadata.email || !metadata.userUUID || !metadata.loanNumber) {
    console.warn("[registration feedback] Incomplete metadata for submission:", metadata);
  }
}

function buildQualtricsRequestBody(
  metadata: RegistrationFeedbackMetadata,
  rating: number,
  comment = "",
): RegistrationQualtricsPayload {
  const deviceInfo = getClientDeviceInfo();
  return {
    values: {
      ...buildRegistrationFeedbackPayload(metadata, rating, comment),
      ...deviceInfo,
    },
  };
}

export function buildRegistrationQualtricsPayload(
  rating: number,
  email?: string,
  options?: RegistrationFeedbackOptions,
): RegistrationQualtricsPayload {
  return buildQualtricsRequestBody(
    {
      email: email ?? "",
      userUUID: options?.userUuid ?? "",
      loanNumber: options?.loanNumber ?? "",
    },
    rating,
    options?.feedbackText ?? "",
  );
}

export async function submitRegistrationFeedback(
  metadata: RegistrationFeedbackMetadata,
  rating: number,
  comment = "",
): Promise<RegistrationFeedbackResult | undefined> {
  warnIfMetadataIncomplete(metadata);

  const surveyId = config.env("registrationNpsSurveyId");
  const qualtricsUrl = config.env("qualtricsUrl");
  const qualtricsAPIKey = config.env("qualtricsAPIKey");

  if (!surveyId || !qualtricsUrl || !qualtricsAPIKey) {
    console.warn(
      "[Registration NPS] Qualtrics config incomplete; skipping survey submission.",
      { surveyId: Boolean(surveyId), qualtricsUrl: Boolean(qualtricsUrl), qualtricsAPIKey: Boolean(qualtricsAPIKey) },
    );
    return;
  }

  const url = `${qualtricsUrl}/${surveyId}/responses`;
  const requestBody = buildQualtricsRequestBody(metadata, rating, comment);

  logAPIEvent({
    action: "APIRequest",
    type: "API",
    request: url,
  });

  let response: AxiosResponse<QualtricsApiResponse>;

  try {
    response = await axios<QualtricsApiResponse>({
      method: "POST",
      url,
      headers: {
        "X-API-TOKEN": qualtricsAPIKey,
        "Content-Type": "application/json",
      },
      data: requestBody,
      withCredentials: false,
      validateStatus: () => true,
    });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    logQualtricsApiResponse(url, false, 0, { errorMessage });
    console.error("[Registration NPS] Failed to submit survey response", error);
    throw error;
  }

  const { status, data } = response;

  if (!isQualtricsSuccessStatus(status)) {
    const errorMessage = getQualtricsErrorMessage(status, data);
    logQualtricsApiResponse(url, false, status, { errorMessage });
    console.error("[Registration NPS] Failed to submit survey response", {
      statusCode: status,
      data,
    });
    throw new Error(errorMessage);
  }

  const result: RegistrationFeedbackResult = {
    statusCode: status,
    responseId: data?.result?.responseId,
    requestId: data?.meta?.requestId,
  };

  logQualtricsApiResponse(url, true, status, { responseId: result.responseId });

  return result;
}

export async function submitRegistrationFeedbackComment(
  metadata: RegistrationFeedbackMetadata,
  rating: number,
  comment: string,
): Promise<RegistrationFeedbackResult | undefined> {
  return submitRegistrationFeedback(metadata, rating, comment);
}
